import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import { axiosPath } from "../../../../routing/helpers/router";
import Translator from "../../../../layout/components/helpers/Translator";
import BreadcrumbHandler from "../../../../layout/components/breadcrumb/BreadcrumbHandler";
import errorHandler from "@/layout/components/error/ErrorHandler";
import ModuleComsaEnvironmentalSideBarReports from "../ModuleComsaEnvironmentalSideBarReports";
import ModuleComsaEnvironmentalConstructionPotentialsAspectsCard from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalConstruction/ModuleComsaEnvironmentalConstructionPotentialsAspectsCard";
import ModuleComsaEnvironmentalConstructionEmergencyAspectsCard from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalConstruction/ModuleComsaEnvironmentalConstructionEmergencyAspectsCard";
import ModuleComsaEnvironmentalTitleCard from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalTitleCard";
import useIsMounted from "../../../../layout/components/hooks/useIsMounted";
import * as currentPageAction from "../../../../store/actions/currentPageAction";
import { AppNoInfo } from "@/layout/AppComponents";

export default function ModuleComsaEnvironmentalConstructionEmergencyPotentialsAspectsScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [values, setValues] = useState();

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "constructionEmergencyPotentialsAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setValues(r.data?.sheetData ?? {});
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="construction"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_construction_emergency_potentials_aspects" />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator
                file="module-comsaEnvironmental"
                name="potential.aspects.bar.emergency"
              />
            </Title>
            <Subtitle className="mb-4">
              <Translator
                file="module-comsaEnvironmental"
                name="potential.aspects"
              />
            </Subtitle>
          </div>
        </div>
        {values && (
          <>
            <ModuleComsaEnvironmentalTitleCard color="warning">
              <h6
                className="mb-0"
                title={ReactDOMServer.renderToString(
                  <Translator file="module-comsaEnvironmental" name="aspect" />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="emissions.particle.emissions"
                />
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionPotentialsAspectsCard
              values={values?.potentialAspects?.demolitions}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="demolitions.non.compliant.elements"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionPotentialsAspectsCard
              values={values?.potentialAspects?.soilMovementExcess}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="excess.earthworks"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="secondary">
              <h6
                className="mb-0"
                title={ReactDOMServer.renderToString(
                  <Translator file="module-comsaEnvironmental" name="aspect" />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="noise.vibrations.noise"
                />
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionPotentialsAspectsCard
              values={values?.potentialAspects?.nightWorks}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="nighttime.work"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionPotentialsAspectsCard
              values={values?.potentialAspects?.noSatisfiedElementsDemolitions}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="demolitions.non.compliant.elements"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionPotentialsAspectsCard
              values={values?.potentialAspects?.soilMovementExcess2}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="excess.earthworks"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="secondary">
              <h6
                className="mb-0"
                title={ReactDOMServer.renderToString(
                  <Translator file="module-comsaEnvironmental" name="aspect" />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="discharges.uncontrolled.discharges"
                />
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionPotentialsAspectsCard
              values={values?.potentialAspects?.floorUncontrolledWastes}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="uncontrolled.soil.discharges.machinery.breakage.failure"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionPotentialsAspectsCard
              values={values?.potentialAspects?.courseUncontrolledWastes}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="uncontrolled.discharges.into.watercourses"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionPotentialsAspectsCard
              values={values?.potentialAspects?.seaWastes}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="discharges.into.sea"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="warning">
              <h6
                className="mb-0"
                title={ReactDOMServer.renderToString(
                  <Translator file="module-comsaEnvironmental" name="aspect" />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="morphology.land.occupation"
                />
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionPotentialsAspectsCard
              values={values?.potentialAspects?.notAllowedAccessUse}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="unauthorized.conditioned.accesses"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionPotentialsAspectsCard
              values={values?.potentialAspects?.soilLandslideSlipping}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="landslides.rockfalls"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="secondary">
              <h6
                className="mb-0"
                title={ReactDOMServer.renderToString(
                  <Translator file="module-comsaEnvironmental" name="aspect" />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="waste.special.waste"
                />
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionPotentialsAspectsCard
              values={values?.potentialAspects?.corruptedSoil}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="pre.existing.contaminated.soils"
                />
              }
            />
            <Subtitle className="mt-5 mb-5">
              <Translator
                file="module-comsaEnvironmental"
                name="emergency.aspects"
              />
            </Subtitle>
            <ModuleComsaEnvironmentalConstructionEmergencyAspectsCard
              values={values?.emergencyAspects?.fires}
              description={
                <Translator file="module-comsaEnvironmental" name="fires" />
              }
            />
            <ModuleComsaEnvironmentalConstructionEmergencyAspectsCard
              values={values?.emergencyAspects?.gasPipingBreak}
              description={
                <Translator
                  file="module-comsaEnvironmental"
                  name="gas.pipeline.breakage"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionEmergencyAspectsCard
              values={values?.emergencyAspects?.waterPipingBreak}
              description={
                <Translator
                  file="module-comsaEnvironmental"
                  name="water.pipeline.breakage"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionEmergencyAspectsCard
              values={values?.emergencyAspects?.sewageSystemBreak}
              description={
                <Translator
                  file="module-comsaEnvironmental"
                  name="sewer.breakage"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionEmergencyAspectsCard
              values={values?.emergencyAspects?.electricalPipingBreak}
              description={
                <Translator
                  file="module-comsaEnvironmental"
                  name="electrical.conduit.breakage"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionEmergencyAspectsCard
              values={values?.emergencyAspects?.telephonePipingBreak}
              description={
                <Translator
                  file="module-comsaEnvironmental"
                  name="telecommunications.conduit.breakage"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionEmergencyAspectsCard
              values={values?.emergencyAspects?.opticalFibrePipingBreak}
              description={
                <Translator
                  file="module-comsaEnvironmental"
                  name="fiber.optic.conduit.breakage"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionEmergencyAspectsCard
              values={values?.emergencyAspects?.securityPipingBreak}
              description={
                <Translator
                  file="module-comsaEnvironmental"
                  name="breakage.safety.signaling.beacon.conduits"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionEmergencyAspectsCard
              values={values?.emergencyAspects?.oilPipingBreak}
              description={
                <Translator
                  file="module-comsaEnvironmental"
                  name="oil.pipeline.breakage"
                />
              }
              showNullValue
            />
            <ModuleComsaEnvironmentalConstructionEmergencyAspectsCard
              values={values?.emergencyAspects?.floods}
              description={
                <Translator file="module-comsaEnvironmental" name="floods" />
              }
            />
            <ModuleComsaEnvironmentalConstructionEmergencyAspectsCard
              values={values?.emergencyAspects?.explosions}
              description={
                <Translator
                  file="module-comsaEnvironmental"
                  name="explosions"
                />
              }
            />
          </>
        )}
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const Subtitle = styled.h4`
  text-align: center;
`;
